import Vue from "vue";
import { http } from "@/communication";
import { uniqueId, union, unionWith } from "lodash";

function newTableFactory(
  url,
  filter,
  id,
  appends,
  name = null,
  additionalRequestData = null
) {
  return Object.assign(
    {},
    {
      id,
      filter: unionWith(
        filter,
        [
          {
            name: "take",
            value: 20
          },
          {
            name: "skip",
            value: 0
          },
          {
            name: "sort",
            mode: "DESC",
            attribute: "created_at"
          }
        ],
        (a, b) => {
          // All the ones who should be added (not union)
          if (a.name === "where" || a.name === "whereDate") {
            return false;
          }

          return a.name === b.name;
        }
      ),
      appends,
      url,
      models: [],
      count: 0,
      name,
      additionalRequestData
    }
  );
}

function modelRequestFactory(table, commit, fetchOnQueue) {
  return http
    .get(table.url, {
      params: {
        filter: table.filter,
        appends: table.appends,
        additional: table.additionalRequestData,
        tableId: table.id,
        fetchOnQueue: fetchOnQueue
      }
    })
    .then(({ data }) => {
      if (!fetchOnQueue) {
        commit("models", { models: data, id: table.id });
      }
    });
}

function countRequestFactory(table, commit, fetchOnQueue) {
  let filter = table.filter.filter(filter => {
    return filter.name !== "take" && filter.name !== "skip";
  });
  return http
    .get(table.url, {
      params: {
        additional: table.additionalRequestData,
        filter: [...filter, { name: "count" }],
        tableId: table.id,
        fetchOnQueue: fetchOnQueue
      }
    })
    .then(({ data }) => {
      if (!fetchOnQueue) {
        commit("count", { count: data, id: table.id });
      }
    });
}

/**
 * buffer request from a table
 *
 * @param {String} name
 */
export const tableModule = {
  namespaced: true,
  state: {
    tables: {}
  },
  actions: {
    new(
      { commit, getters },
      {
        url,
        filter = [],
        appends = [],
        name = "",
        additionalRequestData = null,
        fetchOnQueue = false
      }
    ) {
      return new Promise((resolve, reject) => {
        const id = uniqueId("table-");
        commit("new", {
          url,
          filter,
          id,
          appends,
          name,
          additionalRequestData
        });
        const modelRequest = modelRequestFactory(
          getters.find(id),
          commit,
          fetchOnQueue
        );
        const countRequest = countRequestFactory(
          getters.find(id),
          commit,
          fetchOnQueue
        );
        Promise.all([modelRequest, countRequest])
          .then(() => resolve(id))
          .catch(() => reject());
      });
    },
    update({ commit }, table) {
      return http
        .get(table.url, {
          params: {
            filter: table.filter,
            appends: table.appends,
            additional: table.additionalRequestData
          }
        })
        .then(({ data }) => {
          commit("filter", {
            id: table.id,
            filter: table.filter,
            appends: table.appends
          });
          commit("models", {
            id: table.id,
            models: data
          });
        })
        .catch(error => console.warn(error));
    },
    count({ commit }, table) {
      return countRequestFactory(table, commit);
    },
    newEntry({ commit }, data) {
      let foundTable = null;
      for (let key in this.state.table.tables) {
        if (
          this.state.table.tables.hasOwnProperty(key) &&
          this.state.table.tables[key] !== undefined &&
          this.state.table.tables[key].name === data.tableName
        ) {
          foundTable = this.state.table.tables[key];
        }
      }

      if (foundTable !== null) {
        return http
          .get(foundTable.url, {
            params: {
              filter: union(foundTable.filter, [
                {
                  name: "where",
                  attributes: {
                    id: [Number(data.entryId)]
                  }
                }
              ]),
              appends: foundTable.appends
            }
          })
          .then(({ data }) => {
            commit("addModels", {
              id: foundTable.id,
              models: Array.isArray(data) ? data : [data]
            });
          })
          .catch(error => console.warn(error));
      }
    }
  },
  mutations: {
    new(state, { url, filter, id, appends, name, additionalRequestData }) {
      Vue.set(
        state.tables,
        id,
        newTableFactory(url, filter, id, appends, name, additionalRequestData)
      );
    },
    models(state, { id, models }) {
      Vue.set(state.tables[id], "models", models);
    },
    count(state, { id, count }) {
      Vue.set(state.tables[id], "count", count);
    },
    filter(state, { id, filter }) {
      Vue.set(state.tables[id], "filter", filter);
    },
    delete(state, id) {
      Vue.set(state.tables, id, undefined);
    },
    removeModel(state, { tableId, modelId }) {
      Vue.set(
        state.tables[tableId],
        "models",
        state.tables[tableId].models.filter(model => model.id !== modelId)
      );
    },
    updateModel(state, { tableId, modelId, attributePath, data }) {
      let modelIndex = state.tables[tableId].models.findIndex(
        model => model.id === modelId
      );

      if (modelIndex >= 0) {
        let segments = attributePath.split(".");

        let attributeToChange = segments.pop();

        let value = state.tables[tableId].models[modelIndex];
        segments.forEach(segment => {
          value = value[segment];
        });
        Vue.set(value, attributeToChange, data);
      }
    },
    addModels(state, { id, models }) {
      Vue.set(
        state.tables[id],
        "models",
        unionWith(models, state.tables[id].models, (a, b) => {
          return a.id !== undefined && b.id !== undefined && a.id === b.id;
        })
      );
    }
  },
  getters: {
    find: state => id => {
      return state.tables[id];
    },
    take: state => id => {
      const filters = state.tables[id].filter.filter(filter => {
        return filter.name === "take";
      });
      return filters.length > 0 ? Number(filters[0].value) : 0;
    },
    skip: state => id => {
      const filters = state.tables[id].filter.filter(filter => {
        return filter.name === "skip";
      });
      return filters.length > 0 ? Number(filters[0].value) : 0;
    },
    tableByName: state => name => {
      return Object.keys(state.tables).find(
        id => state.tables[id] && state.tables[id].name === name
      );
    }
  }
};
